import type { types } from '@bedc/types';

import type { Projection } from './types';

/**
 * Lengths of the projected segments in REAL units — the "constants of length"
 * the formula's bindings carry and the pack consumes. Geometry never crosses
 * the formula boundary; it stays engine-side for the derived layer's rendering.
 *
 * THE ONE UNIT-AWARE SEAM: it owns the system → cut-list resolution mapping
 * (imperial: lengths in ft snapped to the inch; metric: lengths in m snapped
 * to 10 mm, matching `formatLength`). Everything below it — projection and
 * pack — is unit-blind arithmetic. Pieces smaller than one resolution step are
 * dropped (the sliver rule).
 *
 * @param scale Real units per drawing unit (the same `effectiveScale` the
 *   measurement pipeline uses: ft/px imperial, m/px metric).
 */
export const segmentLengths = (
	projections: Projection[],
	scale: number,
	measurementSystem: types.ui.MeasurementSystem
): number[] => {
	const resolution = measurementSystem === 'imperial' ? 1 / 12 : 0.01;

	return projections
		.flatMap((projection) => projection.segments)
		.map((s) => Math.round(((s.y1 - s.y0) * scale) / resolution) * resolution)
		.filter((length) => length >= resolution);
};

/**
 * `segmentLengths` per layer, each at its own slope: the plan segments are
 * scaled to true length BEFORE the resolution snap, so rounding and the sliver
 * rule apply to what gets cut, not to its plan shadow.
 *
 * @param projections Each layer's projection with its `slopeFactor` (absent = flat).
 * @param scale Real units per drawing unit, as for `segmentLengths`.
 */
export const slopedSegmentLengths = (
	projections: { projection: Projection; slopeFactor?: number }[],
	scale: number,
	measurementSystem: types.ui.MeasurementSystem
): number[] =>
	projections.flatMap(({ projection, slopeFactor }) =>
		segmentLengths([projection], scale * (slopeFactor ?? 1), measurementSystem)
	);
